import { useEffect, useRef, useState } from 'react';
import { useAuthContext } from '../../hooks/useAuthContext';
import SectionEditor from './SectionEditor';
import TranslationInput from './TranslationInput';
import IconButton from '../IconButton/IconButton';
import Button from '../Button/Button';
import ImageUpload from '../ImageUpload/ImageUpload';
import { apiFetch } from '../../utils/api';
import { uploadToImageKit } from '../../utils/imageKitUpload';
import { emptyTranslation } from '../../utils/translationUtils';
import './ItemEditor.css';

const emptySection = () => ({
  name: emptyTranslation(),
  type: 'string',
  bullets: [],
});

const emptyItem = () => ({
  image: '',
  featured: false,
  name: emptyTranslation(),
  description: emptyTranslation(),
  sections: [emptySection()],
});

const validate = (item, imageFile) => {
  if (!imageFile && !item.image) return 'Please upload an image.';
  if (!item.name.en.trim()) return 'Name (EN) is required.';
  return null;
};

const sortedIds = (cats) => cats.map(c => c._id).sort().join(',');

const ItemEditor = ({
  categoryId,
  itemId,
  initialItem,
  initialCategories,
  onSuccess,
}) => {
  const isEditMode = !!itemId;
  const { user } = useAuthContext();
  const baseline = useRef(initialItem ?? emptyItem());
  const categoriesBaseline = useRef(sortedIds(initialCategories ?? []));
  const pickerRef = useRef(null);

  const [item, setItem] = useState(initialItem ?? emptyItem());
  const [activeTab, setActiveTab] = useState('basic');
  const [imageFile, setImageFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  // Categories state
  const [localCategories, setLocalCategories] = useState(initialCategories ?? []);
  const [allCategories, setAllCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);

  // Picker state
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerSearch, setPickerSearch] = useState('');

  // Fetch all categories; in create mode also set the initial selection
  useEffect(() => {
    const fetchCategories = async () => {
      setCategoriesLoading(true);
      const res = await apiFetch('/api/admin/categories', {
        headers: { Authorization: `Bearer ${user.token}` },
      });
      const data = await res.json();
      setAllCategories(data);
      if (!isEditMode) {
        const current = data.find(c => c._id === categoryId);
        if (current) setLocalCategories([current]);
      }
      setCategoriesLoading(false);
    };
    fetchCategories();
  }, []);

  // Close picker on outside click
  useEffect(() => {
    if (!pickerOpen) return;
    const handler = (e) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target)) {
        setPickerOpen(false);
        setPickerSearch('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [pickerOpen]);

  const categoriesChanged = sortedIds(localCategories) !== categoriesBaseline.current;
  const hasChanges = !isEditMode || !!imageFile || JSON.stringify(item) !== JSON.stringify(baseline.current) || categoriesChanged;
  const isValid = !validate(item, imageFile) && localCategories.length >= 1;

  const handleTranslationChange = (field, lang, value) => {
    setItem(prev => ({ ...prev, [field]: { ...prev[field], [lang]: value } }));
  };

  const handleSectionsChange = (sections) => {
    setItem(prev => ({ ...prev, sections }));
  };

  const handleRemoveImage = () => {
    setImageFile(null);
    setItem(prev => ({ ...prev, image: '' }));
  };

  const handleAddCategory = (cat) => {
    setLocalCategories(prev => [...prev, cat]);
    setPickerOpen(false);
    setPickerSearch('');
  };

  const handleRemoveCategory = (catId) => {
    setLocalCategories(prev => prev.filter(c => c._id !== catId));
  };

  const handleSubmit = async () => {
    const validationError = validate(item, imageFile);
    if (validationError) { setError(validationError); return; }
    if (localCategories.length === 0) { setError('Item must belong to at least one category.'); return; }

    setSubmitting(true);
    setError(null);

    try {
      let imageUrl = item.image;
      let imageFileId = null;

      if (imageFile) {
        ({ url: imageUrl, fileId: imageFileId } = await uploadToImageKit(imageFile, user.token));
      }

      const url = isEditMode
        ? `/api/admin/categories/${categoryId}/items/${itemId}`
        : `/api/admin/categories/${categoryId}/items`;
      const method = isEditMode ? 'PATCH' : 'POST';

      const res = await apiFetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${user.token}`,
        },
        body: JSON.stringify({
          ...item,
          image: imageUrl,
          imageFileId: imageFileId ?? item.imageFileId,
          categories: localCategories.map(c => c._id),
          featured: item.featured,
        }),
      });

      if (!res.ok) {
        let msg = isEditMode ? 'Failed to save changes' : 'Failed to add item';
        try { const e = await res.json(); if (e?.error) msg = e.error; } catch {}
        throw new Error(msg);
      }

      setSuccess(true);
      if (isEditMode) {
        const savedImageFileId = imageFileId ?? item.imageFileId;
        setItem(prev => {
          const next = { ...prev, image: imageUrl, imageFileId: savedImageFileId };
          baseline.current = next;
          return next;
        });
        categoriesBaseline.current = sortedIds(localCategories);
        setImageFile(null);
        onSuccess?.();
        setTimeout(() => setSuccess(false), 2000);
      } else {
        setTimeout(() => onSuccess?.(), 1200);
      }
    } catch (err) {
      setError(err.message || 'Failed to add item');
    } finally {
      setSubmitting(false);
    }
  };

  const imagePreviewSrc = imageFile ? URL.createObjectURL(imageFile) : item.image || null;

  const availableToLink = allCategories.filter(
    c => !localCategories.some(lc => lc._id === c._id)
  );
  const filteredAvailable = availableToLink.filter(c =>
    (c.translations?.en ?? '').toLowerCase().includes(pickerSearch.toLowerCase())
  );

  if (success && !isEditMode) {
    return (
      <div className="item-editor-success">
        <span className="material-symbols-outlined">check_circle</span>
        <p>Item added successfully!</p>
      </div>
    );
  }

  return (
    <div className="item-editor">
      <div className="item-editor-tabs">
        <button className={activeTab === 'basic' ? 'active' : ''} onClick={() => setActiveTab('basic')}>
          Basic Info
        </button>
        <button className={activeTab === 'sections' ? 'active' : ''} onClick={() => setActiveTab('sections')}>
          Sections
        </button>
        <button className={activeTab === 'links' ? 'active' : ''} onClick={() => setActiveTab('links')}>
          Links
        </button>
      </div>

      {activeTab === 'basic' && (
        <div className="item-editor-tab-content">
          <ImageUpload
            previewSrc={imagePreviewSrc}
            onSelect={setImageFile}
            onRemove={handleRemoveImage}
            disabled={submitting}
          />

          <TranslationInput
            label="Name"
            value={item.name}
            onChange={(lang, val) => handleTranslationChange('name', lang, val)}
            disabled={submitting}
          />
          <TranslationInput
            label="Description"
            value={item.description}
            onChange={(lang, val) => handleTranslationChange('description', lang, val)}
            textarea
            disabled={submitting}
          />
          <div className="item-editor-featured">
            <Button
              variant={item.featured ? 'primary' : 'secondary'}
              size="sm"
              onClick={() => setItem(prev => ({ ...prev, featured: !prev.featured }))}
              disabled={submitting}
            >
              <span className="material-symbols-outlined">{item.featured ? 'star' : 'star_border'}</span>
              {item.featured ? 'Featured on homepage' : 'Mark as featured'}
            </Button>
          </div>
        </div>
      )}

      {activeTab === 'sections' && (
        <SectionEditor sections={item.sections} onChange={handleSectionsChange} />
      )}

      {activeTab === 'links' && (
        <div className="item-editor-tab-content item-editor-links">
          {categoriesLoading ? (
            <p className="item-editor-picker-empty">Loading categories…</p>
          ) : (
            <>
              <ul className="item-editor-links-list">
                {localCategories.map(cat => {
                  const name = cat.translations?.en ?? 'Unnamed';
                  const isLast = localCategories.length === 1;
                  return (
                    <li key={cat._id} className="item-editor-link-row">
                      <span className="item-editor-link-name">{name}</span>
                      <IconButton
                        icon="close"
                        variant="danger"
                        disabled={isLast}
                        title={isLast ? 'Item must belong to at least one category' : `Remove from "${name}"`}
                        onClick={() => handleRemoveCategory(cat._id)}
                      />
                    </li>
                  );
                })}
              </ul>

              <div className="item-editor-link-picker-wrap" ref={pickerRef}>
                <Button
                  variant="secondary"
                  className="item-editor-add-link-btn"
                  onClick={() => setPickerOpen(o => !o)}
                  disabled={availableToLink.length === 0}
                  title={availableToLink.length === 0 ? 'Item is already in all categories' : undefined}
                >
                  <span className="material-symbols-outlined">add</span>
                  Add to another category
                </Button>

                {pickerOpen && (
                  <div className="item-editor-link-picker">
                    <input
                      autoFocus
                      className="item-editor-link-search"
                      placeholder="Search categories..."
                      value={pickerSearch}
                      onChange={e => setPickerSearch(e.target.value)}
                    />
                    {filteredAvailable.length === 0
                      ? <p className="item-editor-picker-empty">No categories found</p>
                      : (
                        <ul className="item-editor-picker-list">
                          {filteredAvailable.map(cat => (
                            <li key={cat._id} className="item-editor-picker-option"
                              onClick={() => handleAddCategory(cat)}>
                              {cat.translations?.en ?? 'Unnamed'}
                            </li>
                          ))}
                        </ul>
                      )
                    }
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}

      <div className="item-editor-submit-area">
        {success && isEditMode && <div className="form-message success">Changes saved!</div>}
        {error && <div className="form-message error">{error}</div>}
        <Button variant="primary" className="item-editor-submit-btn" onClick={handleSubmit} disabled={submitting || !hasChanges || !isValid}>
          {submitting ? (isEditMode ? 'Saving...' : 'Adding...') : (isEditMode ? 'Save Changes' : 'Add Item')}
        </Button>
      </div>
    </div>
  );
};

export default ItemEditor;
